import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isValid, parseISO } from 'date-fns';
import { CONTACTS } from '@department-of-veterans-affairs/component-library/contacts';
import { formatDateLong } from 'platform/utilities/date';
import {
  quickSubmitUrls,
  useQuickSubmitRouteLoaderData,
  withBasename,
} from '../utilities/quickSubmit';

// VA.gov technical support line. Not in the component library's CONTACTS.
const HELP_DESK = '8662793677';

const isText = value => typeof value === 'string' && value.trim() !== '';

// History state is whatever a past entry left there, so take only names.
const toFileNames = value => (Array.isArray(value) ? value.filter(isText) : []);

const toDate = value => {
  const date = typeof value === 'string' && value ? parseISO(value) : null;
  return date && isValid(date) ? date : null;
};

// Without both names there's nothing to print, so the page names no one.
const toName = value =>
  isText(value?.firstName) && isText(value?.lastName)
    ? { firstName: value.firstName.trim(), lastName: value.lastName.trim() }
    : null;

const QuickSubmitConfirmationPage = () => {
  const { state } = useLocation();
  const { claimant = null, claimantId = null } =
    useQuickSubmitRouteLoaderData();
  const urls = quickSubmitUrls({ claimantId });

  // Only the embed's onComplete brings a rep here, with the packet id in
  // state. Without one, nothing was sent, so this page must not say it was.
  if (!state?.correlationId) return <Navigate to={urls.upload} replace />;

  const historyHref = claimantId
    ? `/find-claimant/submission-history/${claimantId}`
    : '/submission-history';
  const files = toFileNames(state.files);
  const submittedAt = toDate(state.submittedAt);
  // The name filed with the packet, when the embed sends one. Otherwise the
  // prefilled claimant, which is read-only in the embed. "Last, First", as
  // the rest of ARP names them.
  const named = toName(state.veteran) || toName(claimant);
  const claimantName = named && `${named.lastName}, ${named.firstName}`;

  return (
    <>
      <va-alert status="success" visible>
        <h2 slot="headline">You’ve uploaded your files</h2>
        <p className="vads-u-margin-bottom--0">
          {claimantName ? (
            <>
              You uploaded files for{' '}
              <strong data-testid="qs-confirmation-claimant">
                {claimantName}
              </strong>
            </>
          ) : (
            'You uploaded files'
          )}
          {submittedAt && (
            <>
              {' '}
              on <strong>{formatDateLong(submittedAt)}</strong>
            </>
          )}
          . Your confirmation number is{' '}
          <strong data-testid="qs-confirmation-number">
            {state.correlationId}
          </strong>
          .
        </p>
      </va-alert>

      {files.length > 0 && (
        <section className="vads-u-margin-top--4">
          <h2>What you’ve uploaded</h2>
          <p>
            You uploaded the following{' '}
            {files.length === 1 ? 'file' : `${files.length} files`}:
          </p>
          <ul data-testid="qs-confirmation-files">
            {files.map((name, i) => (
              // Two files can share a name, so the position keeps keys unique.
              <li key={`${i}-${name}`}>{name}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="vads-u-margin-top--4">
        <h2>What to expect next</h2>
        <va-process-list>
          <va-process-list-item header="We’ll identify which forms and documents you uploaded">
            <p>
              We’ll make sure your forms and documents get to the right place.
            </p>
          </va-process-list-item>
          <va-process-list-item header="We’ll review your forms and documents">
            <p>
              We process forms in the order we receive them. This can take some
              time depending on the files.
            </p>
          </va-process-list-item>
        </va-process-list>
        <p>
          You can track the progress of your upload in your submission history.
          It may take up to 10 minutes for your upload to appear.
        </p>
        <va-link-action
          href={withBasename(historyHref)}
          text="Review your submission history"
          type="primary"
          data-testid="qs-confirmation-submissions-link"
        />
        <va-link-action
          class="vads-u-display--block vads-u-margin-top--2"
          href={withBasename(urls.upload)}
          text="Upload more documents"
          type="secondary"
          data-testid="qs-confirmation-upload-link"
        />
      </section>

      {/* va-need-help draws the "Need help?" heading itself. */}
      <div className="vads-u-margin-top--4">
        <va-need-help>
          <div slot="content">
            <p className="vads-u-margin-y--0">
              If you have questions about your submission, call us at{' '}
              <va-telephone contact={HELP_DESK} /> (
              <va-telephone contact={CONTACTS[711]} tty />
              ).
            </p>
          </div>
        </va-need-help>
      </div>
    </>
  );
};

export default QuickSubmitConfirmationPage;
